import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ClockCircleOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  InputNumber,
  Select,
  Space,
  TimePicker,
  Typography,
  Radio,
} from 'antd';
import _, { debounce } from 'lodash';
import moment from 'moment';

import { isAccessEnded } from '../../../../../../../helpers/utils/functions';
import {
  disabledMinutes,
  updateFilterItemInList,
} from '../../../../../../../helpers/utils/reports';
import { useActions } from '../../../../../hooks/useActions';
import { useTypedSelector } from '../../../../../hooks/useTypedSelector';

/** ID фильтров вебинаров для проверки дубликатов (data_webinar исключён — можно выбирать несколько раз) */
const WEBINAR_FILTER_IDS = [
  'uwe_data_webinar',
  'uwe_time_webinar',
  'time_webinar'
];

const WEBINAR_NOT_FILTER_IDS = [
  'not_uwe_time_webinar',
  'not_uwe_data_webinar',
  'not_time_webinar'
];

/** Props для внутреннего DatePicker */
interface DPProps {
  value: moment.Moment | string | null;
  type: 'start' | 'end';
  disabled: boolean;
  format: string;
  showTime: { format: string; hideDisabledOptions: boolean } | false;
  onChange: (date: moment.Moment | null, type: 'start' | 'end') => void;
  disabledDate?: (current: moment.Moment) => boolean;
  disabledTime?: (current: moment.Moment | null) => any;
}

/**
 * DatePicker с автозакрытием при выборе минут (когда showTime включён).
 * Без showTime — стандартное поведение antd (закрывается при клике на дату).
 */
const DP: React.FC<DPProps> = ({ value, type, disabled, format, showTime, onChange, disabledDate, disabledTime }) => {
  const _val = value ? moment(value) : null;
  const [ open, setOpen ] = useState(false);
  const lastMinuteRef = useRef<number | null>(null);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      lastMinuteRef.current = _val ? _val.minute() : null;
    }
  }, [ _val ]);

  const handleSelect = useCallback((val: moment.Moment) => {
    onChange(val, type);
    const newMinute = val.minute();
    if (lastMinuteRef.current !== null && lastMinuteRef.current !== newMinute) {
      setOpen(false);
    }
    lastMinuteRef.current = newMinute;
  }, [ type, onChange ]);

  // Управление открытием — только когда showTime включён
  const openProps = showTime ? {
    open,
    onOpenChange: handleOpenChange,
    onSelect: handleSelect,
  } : {};

  return (
    <DatePicker
      disabled={disabled}
      value={_val}
      onChange={(date) => onChange(date, type)}
      format={format}
      showTime={showTime}
      disabledDate={disabledDate}
      disabledTime={disabledTime}
      disabledMinutes={disabledMinutes}
      style={{ width: 160 }}
      {...openProps}
    />
  );
};

/** Парсит строку времени "HH:mm" в объект {hour, minute, second} */
const parseTimeToObj = (
  timeStr: string | null | undefined,
  fallback: { hour: number; minute: number; second: number }
): { hour: number; minute: number; second: number } => {
  if (!timeStr) return fallback;
  const m = moment(timeStr, 'HH:mm');
  if (!m.isValid()) return fallback;
  return { hour: m.hour(), minute: m.minute(), second: 0 };
};

/** Props для NTP */
interface NTPProps {
  value: string | null;
  placeholder: string;
  disabled: boolean;
  onChange: (val: moment.Moment | null) => void;
  disabledHours?: () => number[];
  getDisabledMinutes?: (hour: number) => number[];
}

/**
 * TimePicker с автозакрытием при выборе минут.
 * При клике на час запоминает lastMinute, при клике на минуту — закрывает.
 */
const NTP: React.FC<NTPProps> = ({ value, placeholder, disabled, onChange, disabledHours, getDisabledMinutes }) => {
  const _val = value ? moment(value, 'HH:mm') : null;
  const [ open, setOpen ] = useState(false);
  const lastMinuteRef = useRef<number | null>(null);

  const handleOpenChange = useCallback((newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      lastMinuteRef.current = _val ? _val.minute() : null;
    }
  }, [ _val ]);

  const handleSelect = useCallback((val: moment.Moment) => {
    onChange(val);
    const newMinute = val.minute();
    if (lastMinuteRef.current !== null && lastMinuteRef.current !== newMinute) {
      setOpen(false);
    }
    lastMinuteRef.current = newMinute;
  }, [ onChange ]);

  return (
    <TimePicker
      disabled={disabled}
      placeholder={placeholder}
      format="HH:mm"
      disabledHours={disabledHours}
      disabledMinutes={getDisabledMinutes || disabledMinutes}
      hideDisabledOptions
      showNow={false}
      value={_val}
      open={open}
      onOpenChange={handleOpenChange}
      onSelect={handleSelect}
      style={{ width: 90 }}
    />
  );
};

/** Интерфейс календарных настроек */
interface CalendarSettings {
  type: string;
  time: boolean;
  nStart: number | null;
  nEnd: number | null;
  nStartTime?: string | null;
  nEndTime?: string | null;
}

/** Интерфейс данных фильтра */
interface FilterData {
  start: moment.Moment | string | null;
  end: moment.Moment | string | null;
  calendar: CalendarSettings;
  [key: string]: unknown;
}

/** Props для DateTimeRangePicker */
interface DateTimeRangePickerProps {
  disabled?: boolean;
  id: string;
  index?: number;
  modalVisibleHandler: (visible: boolean) => void;
  isModal?: boolean;
  isLocalMode?: boolean;
  localFilters?: any[];
  setLocalFilters?: (filters: any[]) => void;
  isYM?: boolean;
  itemData?: FilterData;
  list?: boolean;
  [key: string]: unknown;
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  disabled = false,
  id,
  index,
  modalVisibleHandler,
  isModal = false,
  isLocalMode = false,
  localFilters,
  setLocalFilters,
  isYM = false,
  itemData,
  list,
  ...props
}) => {
  const defaultValue: FilterData = useMemo(() => ({
    start: null,
    end: null,
    calendar: { type: 'selectdates', time: false, nStart: null, nEnd: null },
    ...props,
  }), [props]);

  const { filters: globalFilters } = useTypedSelector((state) => 
    isModal ? state.filter.dashboardModal : isYM ? state.filter.yandexMetric : state.filter
  );
  const filters = isLocalMode ? localFilters || [] : globalFilters;
  const { setFilters, setFilter, setMetaFilterUpdate } = useActions();

  const [filterMode, setFilterMode] = useState(1);

  // Приводим id к строке для безопасной работы с методами строк
  const safeId = String(id ?? '');

  // Вычисляем ID с учётом режима include/exclude
  const filterId = useMemo(() => {
    if (filterMode === 2) {
      return safeId.includes('not_') ? safeId : `not_${safeId}`;
    }
    return safeId.includes('not_') ? safeId.split('not_')[1] : safeId;
  }, [safeId, filterMode]);

  // Инициализация режима из id
  useEffect(() => {
    setFilterMode(safeId.includes('not_') ? 2 : 1);
  }, [safeId]);

  // Определяем режим редактирования
  const isEditing = useMemo(() => {
    return Boolean(list && itemData);
  }, [list, itemData]);

  // Мемоизированные начальные данные фильтра
  const initialFilter = useMemo((): FilterData => {
    if (itemData && list) {
      return itemData;
    }
    return defaultValue;
  }, [itemData, list, defaultValue]);

  const [currentData, setCurrentData] = useState<FilterData>(initialFilter);

  // Синхронизация currentData при изменении itemData
  useEffect(() => {
    if (itemData && list) {
      setCurrentData(itemData);
    }
  }, [itemData, list]);

  // Мемоизированные форматы
  const format = useMemo(() => 
    `DD.MM.YYYY${currentData.calendar.time ? ' HH:mm' : ''}`,
    [currentData.calendar.time]
  );

  // Время включается только когда пользователь явно его выбрал
  const hasTime = useMemo(() => {
    if (currentData.calendar.type === 'nhoursago') return true;
    if (currentData.calendar.type === 'ndaysago') return currentData.calendar.nStartTime != null;
    return currentData.calendar.time;
  }, [currentData.calendar.type, currentData.calendar.nStartTime, currentData.calendar.time]);

  const finalFormat = useMemo(() =>
    hasTime ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD',
    [hasTime]
  );

  const showTime = useMemo(() => 
    currentData.calendar.time ? { format: 'HH:mm', hideDisabledOptions: true } : false,
    [currentData.calendar.time]
  );

  const onApply = useCallback(() => {
    const _currentData = {
      ...currentData,
      id: filterId,
      start: currentData.start
        ? moment(currentData.start).format(finalFormat)
        : moment().format(finalFormat),
      end: currentData.end
        ? moment(currentData.end).format(finalFormat)
        : moment().format(finalFormat),
    };

    if (isLocalMode && setLocalFilters) {
      // Локальный режим - используем callback
      if (isEditing && index !== undefined) {
        let _filters = _.cloneDeep(filters);
        _filters = updateFilterItemInList(_filters, index, _currentData);
        setLocalFilters(_filters);
      } else {
        // Проверка на дубликаты для фильтров вебинаров
        if (WEBINAR_FILTER_IDS.includes(filterId) || WEBINAR_NOT_FILTER_IDS.includes(filterId)) {
          const duplicateFilter = filters.find((item) => (item as { id?: string }).id === filterId);
          if (duplicateFilter) {
            modalVisibleHandler(false);
            return;
          }
        }
        setLocalFilters([...filters, _currentData]);
      }
    } else {
      // Глобальный режим - используем Redux
      if (isEditing && index !== undefined) {
        let _filters = _.cloneDeep(filters);
        _filters = updateFilterItemInList(_filters, index, _currentData);
        setFilters(isModal ? { isModal, data: _filters } : isYM ? { isYM, data: _filters } : _filters);
        setMetaFilterUpdate({ value: true });
        modalVisibleHandler(false);
        return;
      }

      // Проверка на дубликаты для фильтров вебинаров
      if (WEBINAR_FILTER_IDS.includes(filterId) || WEBINAR_NOT_FILTER_IDS.includes(filterId)) {
        const duplicateFilter = filters.find((item) => (item as { id?: string }).id === filterId);
        if (duplicateFilter) {
          modalVisibleHandler(false);
          return;
        }
      }

      setFilter({ ..._currentData, isModal, isYM });
      setMetaFilterUpdate({ value: true });
    }
    modalVisibleHandler(false);
  }, [currentData, filterId, finalFormat, isEditing, index, filters, isLocalMode, setLocalFilters, isModal, isYM, setFilters, setFilter, setMetaFilterUpdate, modalVisibleHandler]);

  const onChangeDate = useCallback((date: moment.Moment | null, type: 'start' | 'end') => {
    if (type === 'start') {
      if (!currentData.calendar.time && date) date.set(startTime);
      setCurrentData((prevState) => ({
        ...prevState,
        start: date,
      }));
    } else if (type === 'end') {
      if (!currentData.calendar.time && date) date.set(endTime);
      setCurrentData((prevState) => ({
        ...prevState,
        end: date,
      }));
    }
  }, [currentData.calendar.time]);

  const onChangeType = useCallback((val: string) => {
    const { start, end } = getStartEndType({ calendar: { type: val } });

    setCurrentData((prevState) => ({
      ...prevState,
      calendar: { ...prevState.calendar, nStart: 0, nEnd: 0, nStartTime: null, nEndTime: null, type: val },
      start,
      end,
    }));
  }, []);

  const onClickTime = useCallback(() => {
    setCurrentData((prevState) => ({
      ...prevState,
      calendar: { ...prevState.calendar, time: !prevState.calendar.time },
    }));
  }, []);

  // Переключение выбора времени в режиме "N дней назад"
  const onClickNTime = useCallback(() => {
    setCurrentData((prevState) => {
      const hasTime = prevState.calendar.nStartTime != null;
      const newCalendar = hasTime
        ? { ...prevState.calendar, nStartTime: null, nEndTime: null }
        : { ...prevState.calendar, nStartTime: '00:00', nEndTime: '23:55' };

      const _nStart = prevState.calendar.nStart ?? 0;
      const _nEnd = prevState.calendar.nEnd ?? 0;
      const sTime = parseTimeToObj(newCalendar.nStartTime, startTime);
      const eTime = parseTimeToObj(newCalendar.nEndTime, endTime);
      let start = moment().subtract(_nStart, 'days').set(sTime);
      let end = moment().subtract(_nEnd, 'days').set(eTime);
      if (start.isAfter(end)) [start, end] = [end, start];

      return { ...prevState, start, end, calendar: newCalendar };
    });
  }, []);

  // Изменение времени в режиме "N дней назад"
  const onChangeNTime = useCallback((nType: 'nStartTime' | 'nEndTime', val: moment.Moment | null) => {
    const timeStr = val ? val.format('HH:mm') : null;
    setCurrentData((prevState) => {
      const newCalendar = { ...prevState.calendar, [nType]: timeStr };

      if (prevState.calendar.type === 'ndaysago') {
        const _nStart = prevState.calendar.nStart ?? 0;
        const _nEnd = prevState.calendar.nEnd ?? 0;
        const sTime = parseTimeToObj(
          nType === 'nStartTime' ? timeStr : prevState.calendar.nStartTime, startTime
        );
        const eTime = parseTimeToObj(
          nType === 'nEndTime' ? timeStr : prevState.calendar.nEndTime, endTime
        );
        let start = moment().subtract(_nStart, 'days').set(sTime);
        let end = moment().subtract(_nEnd, 'days').set(eTime);
        if (start.isAfter(end)) [start, end] = [end, start];
        return { ...prevState, start, end, calendar: newCalendar };
      }

      return { ...prevState, calendar: newCalendar };
    });
  }, []);

  const onChangeN = useCallback((nType: 'nStart' | 'nEnd', val: number | null) => {
    setCurrentData((prevState) => {
      let _nStart = prevState.calendar.nStart ?? 0;
      let _nEnd = prevState.calendar.nEnd ?? 0;
      
      if (nType === 'nStart') _nStart = val ?? 0;
      if (nType === 'nEnd') _nEnd = val ?? 0;
      
      let start = moment();
      let end = moment();
      
      if (prevState.calendar.type === 'ndaysago') {
        const sTime = parseTimeToObj(prevState.calendar.nStartTime, startTime);
        const eTime = parseTimeToObj(prevState.calendar.nEndTime, endTime);
        start = start.subtract(_nStart, 'days').set(sTime);
        end = end.subtract(_nEnd, 'days').set(eTime);
        if (start.isAfter(end)) {
          [start, end] = [end, start];
        }
      }
      if (prevState.calendar.type === 'nhoursago') {
        start = start.subtract(_nEnd + _nStart, 'hours');
        end = end.subtract(_nStart, 'hours');
      }

      let newVal = val ?? 0;
      let newNEnd = prevState.calendar.nEnd;
      let newNStart = prevState.calendar.nStart;

      if (
        nType === 'nStart' &&
        prevState.calendar.type === 'ndaysago' &&
        prevState.calendar.nEnd &&
        newVal < prevState.calendar.nEnd
      ) {
        [newVal, newNEnd] = [prevState.calendar.nEnd, newVal];
      } else if (
        nType === 'nEnd' &&
        prevState.calendar.type === 'ndaysago' &&
        prevState.calendar.nStart &&
        newVal > prevState.calendar.nStart
      ) {
        [newVal, newNStart] = [prevState.calendar.nStart, newVal];
      }

      return {
        ...prevState,
        start,
        end,
        calendar: { 
          ...prevState.calendar, 
          [nType]: newVal,
          ...(nType === 'nStart' && newNEnd !== undefined ? { nEnd: newNEnd } : {}),
          ...(nType === 'nEnd' && newNStart !== undefined ? { nStart: newNStart } : {}),
        },
      };
    });
  }, []);

  const debouncedOnChangeN = useMemo(
    () => debounce((nType: 'nStart' | 'nEnd', val: number | null) => onChangeN(nType, val), 300),
    [onChangeN]
  );

  // --- Ограничения: ОТ всегда раньше ДО ---
  const baseDisabled = useMemo(() => disabledMinutes(), []);

  const startDisabledDate = useCallback((current: moment.Moment) => {
    if (!currentData.end || !current) return false;
    return current.isAfter(moment(currentData.end), 'day');
  }, [currentData.end]);

  const endDisabledDate = useCallback((current: moment.Moment) => {
    if (!currentData.start || !current) return false;
    return current.isBefore(moment(currentData.start), 'day');
  }, [currentData.start]);

  const startDisabledTime = useCallback((current: moment.Moment | null) => {
    if (!current || !currentData.end) {
      return { disabledMinutes: () => baseDisabled };
    }
    const endM = moment(currentData.end);
    if (!current.isSame(endM, 'day')) {
      return { disabledMinutes: () => baseDisabled };
    }
    const eH = endM.hour();
    const eMin = endM.minute();
    return {
      disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(h => h > eH),
      disabledMinutes: (hour: number) => {
        if (hour < eH) return baseDisabled;
        if (hour === eH) {
          const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m > eMin);
          return [ ...new Set([ ...baseDisabled, ...range ]) ];
        }
        return Array.from({ length: 60 }, (_, i) => i);
      },
    };
  }, [currentData.end, baseDisabled]);

  const endDisabledTime = useCallback((current: moment.Moment | null) => {
    if (!current || !currentData.start) {
      return { disabledMinutes: () => baseDisabled };
    }
    const startM = moment(currentData.start);
    if (!current.isSame(startM, 'day')) {
      return { disabledMinutes: () => baseDisabled };
    }
    const sH = startM.hour();
    const sMin = startM.minute();
    return {
      disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(h => h < sH),
      disabledMinutes: (hour: number) => {
        if (hour > sH) return baseDisabled;
        if (hour === sH) {
          const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m < sMin);
          return [ ...new Set([ ...baseDisabled, ...range ]) ];
        }
        return Array.from({ length: 60 }, (_, i) => i);
      },
    };
  }, [currentData.start, baseDisabled]);

  // --- Ограничения для времени в режиме ndaysago ---
  // Когда nStart === nEnd (один и тот же день), время ОТ не может быть позже ДО
  const nStartDisabledHours = useCallback(() => {
    const nS = currentData.calendar.nStart ?? 0;
    const nE = currentData.calendar.nEnd ?? 0;
    if (nS !== nE || !currentData.calendar.nEndTime) return [];
    const endM = moment(currentData.calendar.nEndTime, 'HH:mm');
    if (!endM.isValid()) return [];
    return Array.from({ length: 24 }, (_, i) => i).filter(h => h > endM.hour());
  }, [currentData.calendar.nStart, currentData.calendar.nEnd, currentData.calendar.nEndTime]);

  const nStartGetDisabledMinutes = useCallback((hour: number) => {
    const nS = currentData.calendar.nStart ?? 0;
    const nE = currentData.calendar.nEnd ?? 0;
    if (nS !== nE || !currentData.calendar.nEndTime) return baseDisabled;
    const endM = moment(currentData.calendar.nEndTime, 'HH:mm');
    if (!endM.isValid()) return baseDisabled;
    if (hour < endM.hour()) return baseDisabled;
    if (hour === endM.hour()) {
      const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m > endM.minute());
      return [ ...new Set([ ...baseDisabled, ...range ]) ];
    }
    return Array.from({ length: 60 }, (_, i) => i);
  }, [currentData.calendar.nStart, currentData.calendar.nEnd, currentData.calendar.nEndTime, baseDisabled]);

  const nEndDisabledHours = useCallback(() => {
    const nS = currentData.calendar.nStart ?? 0;
    const nE = currentData.calendar.nEnd ?? 0;
    if (nS !== nE || !currentData.calendar.nStartTime) return [];
    const startM = moment(currentData.calendar.nStartTime, 'HH:mm');
    if (!startM.isValid()) return [];
    return Array.from({ length: 24 }, (_, i) => i).filter(h => h < startM.hour());
  }, [currentData.calendar.nStart, currentData.calendar.nEnd, currentData.calendar.nStartTime]);

  const nEndGetDisabledMinutes = useCallback((hour: number) => {
    const nS = currentData.calendar.nStart ?? 0;
    const nE = currentData.calendar.nEnd ?? 0;
    if (nS !== nE || !currentData.calendar.nStartTime) return baseDisabled;
    const startM = moment(currentData.calendar.nStartTime, 'HH:mm');
    if (!startM.isValid()) return baseDisabled;
    if (hour > startM.hour()) return baseDisabled;
    if (hour === startM.hour()) {
      const range = Array.from({ length: 60 }, (_, i) => i).filter(m => m < startM.minute());
      return [ ...new Set([ ...baseDisabled, ...range ]) ];
    }
    return Array.from({ length: 60 }, (_, i) => i);
  }, [currentData.calendar.nStart, currentData.calendar.nEnd, currentData.calendar.nStartTime, baseDisabled]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !disabled) {
      e.preventDefault();
      onApply();
    }
  }, [disabled, onApply]);

  return (
    <Space direction="vertical" style={{ width: 390 }} onKeyDown={handleKeyDown}>
      <div style={{ marginTop: 8 }}>
        <Radio.Group
          disabled={disabled}
          onChange={(e) => setFilterMode(e.target.value)}
          value={filterMode}
        >
          <Radio value={1}>Включить</Radio>
          <Radio value={2}>Исключить</Radio>
        </Radio.Group>
      </div>
      <div>
        <Select
          disabled={disabled}
          value={currentData.calendar.type}
          onChange={onChangeType}
          style={{ width: '100%', marginBottom: 5 }}
        >
          <Select.Option value="selectdates">выбрать даты</Select.Option>
          <Select.Option value="today">сегодня</Select.Option>
          <Select.Option value="yesterday">вчера</Select.Option>
          <Select.Option value="thisweek">эта неделя</Select.Option>
          <Select.Option value="lastweek">прошлая неделя</Select.Option>
          <Select.Option value="thismonth" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            этот месяц
          </Select.Option>
          <Select.Option value="lastmonth" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            прошлый месяц
          </Select.Option>
          <Select.Option value="thisquart" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            этот квартал
          </Select.Option>
          <Select.Option value="lastquart" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            прошлый квартал
          </Select.Option>
          <Select.Option value="thisyear" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            этот год
          </Select.Option>
          <Select.Option value="lastyear" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            прошлый год
          </Select.Option>
          <Select.Option value="last7days">последние 7 дней</Select.Option>
          <Select.Option value="last14days" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            последние 14 дней
          </Select.Option>
          <Select.Option value="last30days" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            последние 30 дней
          </Select.Option>
          <Select.Option value="last365days" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            последние 365 дней
          </Select.Option>
          <Select.Option value="ndaysago" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            N дней назад
          </Select.Option>
          <Select.Option value="nhoursago" disabled={isAccessEnded()}>
            {isAccessEnded() && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1em"
                height="1em"
                viewBox="0 0 24 24"
              >
                <path
                  fill="currentColor"
                  d="M6 22q-.825 0-1.412-.587T4 20V10q0-.825.588-1.412T6 8h1V6q0-2.075 1.463-3.537T12 1t3.538 1.463T17 6v2h1q.825 0 1.413.588T20 10v10q0 .825-.587 1.413T18 22zm6-5q.825 0 1.413-.587T14 15t-.587-1.412T12 13t-1.412.588T10 15t.588 1.413T12 17M9 8h6V6q0-1.25-.875-2.125T12 3t-2.125.875T9 6z"
                />
              </svg>
            )}
            N часов назад
          </Select.Option>
        </Select>
      </div>
      <div>
        {currentData.calendar.type === 'selectdates' && (
          <Space size="small">
            <Button
              disabled={disabled}
              onClick={onClickTime}
              icon={<ClockCircleOutlined />}
              type={currentData.calendar.time ? 'link' : 'text'}
            />
            <DP value={currentData.start} type="start" disabled={disabled} format={format} showTime={showTime} onChange={onChangeDate} disabledDate={startDisabledDate} disabledTime={startDisabledTime} />
            <Typography.Text type="secondary">—</Typography.Text>
            <DP value={currentData.end} type="end" disabled={disabled} format={format} showTime={showTime} onChange={onChangeDate} disabledDate={endDisabledDate} disabledTime={endDisabledTime} />
          </Space>
        )}
        {[ 'ndaysago', 'nhoursago' ].includes(currentData.calendar.type) && (
          <Space size="small" direction="vertical">
            <Space size="small">
              <Typography.Text type="secondary">от</Typography.Text>
              <InputNumber
                min={0}
                disabled={disabled}
                value={currentData.calendar.nStart}
                onChange={(val) => debouncedOnChangeN('nStart', val)}
                style={{ width: 100 }}
              />
              <Typography.Text type="secondary">до</Typography.Text>
              <InputNumber
                min={0}
                disabled={disabled}
                value={currentData.calendar.nEnd}
                onChange={(val) => debouncedOnChangeN('nEnd', val)}
                style={{ width: 100 }}
              />
            </Space>
            {currentData.calendar.type === 'ndaysago' && (
              <Space size="small">
                <Button
                  disabled={disabled}
                  onClick={onClickNTime}
                  icon={<ClockCircleOutlined />}
                  type={currentData.calendar.nStartTime != null ? 'link' : 'text'}
                  size="small"
                />
                {currentData.calendar.nStartTime != null && (
                  <>
                    <NTP
                      disabled={disabled}
                      placeholder="с"
                      value={currentData.calendar.nStartTime}
                      onChange={(val) => onChangeNTime('nStartTime', val)}
                      disabledHours={nStartDisabledHours}
                      getDisabledMinutes={nStartGetDisabledMinutes}
                    />
                    <Typography.Text type="secondary">—</Typography.Text>
                    <NTP
                      disabled={disabled}
                      placeholder="по"
                      value={currentData.calendar.nEndTime}
                      onChange={(val) => onChangeNTime('nEndTime', val)}
                      disabledHours={nEndDisabledHours}
                      getDisabledMinutes={nEndGetDisabledMinutes}
                    />
                  </>
                )}
              </Space>
            )}
          </Space>
        )}
      </div>
      {![ 'selectdates', 'allthetime' ].includes(currentData.calendar.type) && (
        <ShowStartEnd
          start={currentData.start}
          end={currentData.end}
          calendar={currentData.calendar}
        />
      )}
      <Button
        disabled={disabled}
        type="primary"
        style={{ marginTop: 10 }}
        onClick={onApply}
      >
        Применить
      </Button>
    </Space>
  );
};
const startTime = { hour: 0, minute: 0, second: 0 };
const endTime = { hour: 23, minute: 59, second: 59 };
const FORMAT_VIEW_TIME = 'DD MMM YYYY HH:mm';
const FORMAT_VIEW_DATE = 'DD MMM YYYY';

interface ShowStartEndProps {
  start: moment.Moment | string | null;
  end: moment.Moment | string | null;
  calendar: CalendarSettings;
}

function ShowStartEnd({ start: _start, end: _end, calendar }: ShowStartEndProps) {
  const { start, end } = getStartEndType({
    start: _start,
    end: _end,
    calendar,
  });

  // Время в превью только если оно явно выбрано для данного режима
  // calendar.time не учитываем — он относится к режиму selectdates, а ShowStartEnd не рендерится для selectdates
  const showTime = calendar.type === 'nhoursago'
    || (calendar.type === 'ndaysago' && calendar.nStartTime != null);
  const viewFmt = showTime ? FORMAT_VIEW_TIME : FORMAT_VIEW_DATE;

  const showStartDate = useMemo(
    () => moment(start).format('dd').toUpperCase(),
    [ start ]
  );
  const showStartTime = useMemo(
    () => moment(start).format(viewFmt),
    [ start, viewFmt ]
  );
  const showEndDate = useMemo(
    () => moment(end).format('dd').toUpperCase(),
    [ end ]
  );
  const showEndTime = useMemo(() => moment(end).format(viewFmt), [ end, viewFmt ]);

  return (
    <Space size="small">
      <div>
        {showStartDate}&nbsp;{showStartTime}
      </div>
      <Typography.Text type="secondary">—</Typography.Text>
      <div>
        {showEndDate}&nbsp;{showEndTime}
      </div>
    </Space>
  );
}

interface GetStartEndTypeParams {
  start?: moment.Moment | string | null;
  end?: moment.Moment | string | null;
  calendar: { type: string; nStart?: number | null; nEnd?: number | null; nStartTime?: string | null; nEndTime?: string | null };
}

export const getStartEndType = ({
  start: inputStart = null,
  end: inputEnd = null,
  calendar,
}: GetStartEndTypeParams): { start: moment.Moment | null; end: moment.Moment | null } => {
  const { type } = calendar;
  let _start: moment.Moment | null = null;
  let _end: moment.Moment | null = null;

  if (type === 'selectdates') {
    // Для "выбрать даты" используем то, что пользователь выбрал в пикере.
    // В state это может быть moment или строка (YYYY-MM-DD / YYYY-MM-DD HH:mm:ss).
    _start = inputStart ? moment(inputStart) : null;
    _end = inputEnd ? moment(inputEnd) : null;
  } else if (type === 'today') {
    _start = moment().set(startTime);
    _end = moment().set(endTime);
  } else if (type === 'yesterday') {
    _start = moment().subtract(1, 'days').set(startTime);
    _end = moment().subtract(1, 'days').set(endTime);
  } else if (type === 'last7days') {
    _start = moment().subtract(6, 'days').set(startTime);
    _end = moment().set(endTime);
  } else if (type === 'last14days') {
    _start = moment().subtract(13, 'days').set(startTime);
    _end = moment().set(endTime);
  } else if (type === 'thisweek') {
    _start = moment().isoWeekday(1).set(startTime);
    _end = moment().set(endTime);
  } else if (type === 'lastweek') {
    _start = moment().subtract(1, 'week').isoWeekday(1).set(startTime);
    _end = moment().subtract(1, 'week').isoWeekday(7).set(endTime);
  } else if (type === 'thismonth') {
    _start = moment().startOf('month').set(startTime);
    _end = moment().endOf('month').set(endTime);
  } else if (type === 'lastmonth') {
    _start = moment().subtract(1, 'month').startOf('month').set(startTime);
    _end = moment().subtract(1, 'month').endOf('month').set(endTime);
  } else if (type === 'last30days') {
    _start = moment().subtract(29, 'days').set(startTime);
    _end = moment().set(endTime);
  } else if (type === 'thisquart') {
    _start = moment().startOf('quarters').set(startTime);
    _end = moment().set(startTime);
  } else if (type === 'lastquart') {
    _start = moment().subtract(1, 'quarters').startOf('quarters').set(startTime);
    _end = moment().subtract(1, 'quarters').endOf('quarters').set(startTime);
  } else if (type === 'thisyear') {
    _start = moment().startOf('year').set(startTime);
    _end = moment();
  } else if (type === 'lastyear') {
    _start = moment().subtract(1, 'year').startOf('year').set(startTime);
    _end = moment().subtract(1, 'year').endOf('year').set(startTime);
  } else if (type === 'last365days') {
    _start = moment().subtract(365, 'days').set(startTime);
    _end = moment().set(endTime);
  } else if (type === 'ndaysago') {
    const { nStart, nEnd, nStartTime, nEndTime } = calendar;
    const sTime = parseTimeToObj(nStartTime, startTime);
    const eTime = parseTimeToObj(nEndTime, endTime);
    _start = moment().subtract(nStart, 'days').set(sTime);
    _end = moment().subtract(nEnd, 'days').set(eTime);
  } else if (type === 'nhoursago') {
    const { nStart, nEnd } = calendar;
    _start = moment().subtract(nEnd, 'hours');
    _end = moment().subtract(nStart, 'hours');
  }
  return { start: _start, end: _end };
};

export default DateTimeRangePicker;
